/**
 * The telephone, as a dispatcher uses it.
 *
 * Deliberately small when nothing is happening and unmissable when something is. A
 * dispatcher is looking at the queue, the map and an order form; the telephone earns space
 * on that screen only while it is ringing or connected.
 *
 * Every control on the common path — answer, hang up — is a button rather than a keyboard
 * shortcut, because the hands that would press a shortcut are typing an address.
 *
 * Two shapes. A dispatcher answering in the browser gets the SIP telephone: answer, mute,
 * keypad, transfer, and a second caller shown while the first is up. A dispatcher answering
 * on a desk phone gets the console for everything but the audio: which desk, whether it can
 * be reached, who they are talking to, and the two moves the handset cannot make itself —
 * hold on the PBX and waiting for a car. Both holds are the server's: it moves the caller,
 * so the same buttons work whichever telephone is carrying the call.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, errorKey } from '@taxi/api-client';

import { useDispatch } from '../shell/dispatchContext';
import { usePhone } from '../shell/softphoneContext';
import { useTelephoneContext } from '../shell/telephoneContext';
import {
  Banner,
  Button,
  Card,
  CardHeader,
  Icon,
  Input,
  Pill,
  Segmented,
  Select,
  Switch,
  type Tone,
} from '../ui';
import { callsApi } from '../calls/api';
import { isSameCaller } from '../calls/sameCaller';
import { telephoneApi, type AnswerOn } from './api';
import { canAsk, messageKey, needsAttention } from './microphone';
import { useMicrophone } from './useMicrophone';
import type { CallState, Devices, Softphone as Phone } from './useSoftphone';

export function Softphone({
  orderOnCall,
  caller,
  className,
}: {
  /**
   * The order taken on the call that is up, when there is one.
   *
   * Both halves are needed: the call is what gets parked, and the order is what the caller
   * will be waiting for. Without an order there is nothing for them to wait for, which is
   * exactly what the API refuses.
   */
  orderOnCall?: { callId: string; orderId: string } | null;

  /**
   * Who the number belongs to, when the screen-pop said.
   *
   * SIP knows a number; the pop knew a name, and a dispatcher reading the card back mid-call
   * wants the name. Passed in rather than looked up, because the view already holds it.
   */
  caller?: string | null;
  className?: string;
}) {
  const { t } = useTranslation();

  // Asked passively on mount and again whenever the browser's answer changes. Nothing here
  // opens a dialog; only the button in the banner does.
  const microphone = useMicrophone();

  const {
    registration,
    call,
    callFlow,
    lostOnReload,
    acknowledgeLoss,
    answer,
    answerFailed,
    acknowledgeAnswerFailure,
    hangUp,
    hold,
    mute,
    transfer,
    dtmf,
    devices,
    chooseDevices,
    waiting,
    answerWaiting,
    rejectWaiting,
    heldHere,
    resumeHeld,
  } = usePhone();

  const failure = useFailure(registration);

  const telephone = useTelephoneContext();
  const { mine, myCall } = telephone;

  const { popped } = useDispatch();

  const [transferTo, setTransferTo] = useState('');
  const [keypad, setKeypad] = useState(false);
  const [settings, setSettings] = useState(false);
  const [parking, setParking] = useState(false);
  const [holding, setHolding] = useState(false);

  /**
   * What to tell the dispatcher about the caller they just tried to park.
   *
   * Tagged with the call it is about rather than cleared when the call changes, so that a
   * new caller never inherits the last one's note — "this is a SIM, tell them you will ring
   * back" is exactly the sentence somebody would act on for the wrong person.
   */
  const [parked, setParked] = useState<{ callId: string; text: string } | null>(null);

  const parkedNote = parked !== null && parked.callId === orderOnCall?.callId ? parked.text : null;

  const browserExtension =
    registration.state === 'registered' || registration.state === 'reconnecting'
      ? registration.extension
      : null;

  /** The call the browser is carrying, when the server knows which one it is. */
  const browserCall =
    myCall !== null && myCall.extension === browserExtension ? myCall : null;

  /** The call one of the desk's phones is carrying. */
  const deskCall =
    myCall !== null &&
    myCall.extension !== null &&
    (mine?.desk?.extensions.includes(myCall.extension) ?? false)
      ? myCall
      : null;

  // The desk shape while the browser has nothing of its own to show. A call ringing the
  // browser — the fallback, or the other telephone joining in late — is the browser's.
  const deskShape =
    telephone.onDesk && call.state === 'idle' && waiting === null && heldHere === null;

  /**
   * Parks the caller for a car, if the API agrees.
   *
   * The API moves them, having decided there is room — a desk phone cannot be made to
   * transfer anybody, so the browser no longer does it either. On a SIM line it says to
   * ring back instead, and nobody moves.
   */
  async function park() {
    if (orderOnCall == null) return;

    const { callId } = orderOnCall;

    setParking(true);
    setParked(null);

    try {
      const decision = await callsApi.hold(callId);

      setParked({
        callId,
        text: decision.mode === 'ringBack' ? t('softphone.parkOnSim') : t('softphone.parked'),
      });
    } catch (failure) {
      setParked({
        callId,
        text:
          failure instanceof ApiError
            ? t(errorKey(failure.problem), {
                ...failure.problem.arguments,
                defaultValue: t('softphone.couldNotPark'),
              })
            : t('softphone.couldNotPark'),
      });
    } finally {
      setParking(false);
    }
  }

  /** What the last hold on the PBX said, when it did not work. */
  const [holdFailed, setHoldFailed] = useState<string | null>(null);

  /**
   * Hold, on the PBX.
   *
   * The caller hears the firm's music and this telephone is free: another call can be
   * answered, and anybody in the office can take this caller back. A call the server does
   * not know as this dispatcher's — one they placed themselves — is held in the browser as
   * before, because the PBX would be holding the dispatcher rather than the customer.
   */
  async function holdOnPbx(callId: string) {
    setHolding(true);
    setHoldFailed(null);

    try {
      await telephoneApi.park(callId);
    } catch (failure) {
      setHoldFailed(
        failure instanceof ApiError
          ? t(errorKey(failure.problem), { defaultValue: t('softphone.couldNotHold') })
          : t('softphone.couldNotHold'),
      );
    } finally {
      setHolding(false);
    }
  }

  /** Answers a second caller, putting the first on hold on the PBX when it can. */
  function takeSecond() {
    void answerWaiting(async () => {
      if (browserCall === null) return false;

      try {
        await telephoneApi.park(browserCall.callId);
        return true;
      } catch {
        return false;
      }
    });
  }

  // A firm with no telephone system is the ordinary case for most of this project's life,
  // and an error card on every console would be noise. It simply has no telephone in it —
  // unless the dispatcher answers on a desk phone, which needs none of the browser's.
  if (
    registration.state === 'failed' &&
    registration.code === 'telephony.not_enabled' &&
    !telephone.onDesk
  ) {
    return null;
  }

  return (
    <Card className={className}>
      <CardHeader
        title={
          <>
            <Icon name="phone" />
            {t('softphone.title')}
          </>
        }
        actions={
          <>
            {deskShape && mine?.desk != null ? (
              <Pill tone={mine.deskReachable ? 'ok' : 'danger'} dot>
                {deskCall !== null ? (
                  <>
                    {t('softphone.onACall')} · <Elapsed since={deskCall.since} />
                  </>
                ) : (
                  t('softphone.onDesk', {
                    desk: mine.desk.name,
                    extension: mine.desk.extensions.join(', '),
                  })
                )}
              </Pill>
            ) : (
              <State registration={registration} call={call} />
            )}
            <Button
              size="sm"
              variant="quiet"
              icon
              aria-expanded={settings}
              aria-label={t('softphone.devices')}
              onClick={() => setSettings((open) => !open)}
            >
              <Icon name="chevron" small />
            </Button>
          </>
        }
      />

      {/*
        * Before a customer is on the line, not during.
        *
        * A browser asks for the microphone at the moment a page first wants one, which
        * without this is the moment a call is answered — so the dispatcher meets a
        * permission dialog with a customer already waiting, and the call fails while they
        * read it. Refused or absent, the failure is worse than useless: JsSIP reports every
        * cause as "User Denied Media Access", so a missing device reads as a refusal and an
        * unanswered prompt reads as nothing at all until the caller gives up.
        *
        * `danger` rather than `warn` when the desk cannot be fixed from here: refused and
        * missing both need somebody to leave the page, and a warning tone understates that.
        *
        * Not while answering on a desk phone, unless the browser is the fallback: a
        * dispatcher who never talks through the browser has no use for its microphone.
        */}
      {needsAttention(microphone.readiness) &&
        (!telephone.onDesk || mine?.ringOtherWhenUnavailable === true) && (
        <Banner
          tone={canAsk(microphone.readiness) ? 'warn' : 'danger'}
          stack
          actions={
            canAsk(microphone.readiness) ? (
              /*
               * Solid, green, and pulsing, which is more than a banner action usually
               * earns. It is the one thing standing between a dispatcher and a working
               * telephone, and the cost of missing it is a customer on a silent line —
               * so it is allowed to ask for the eye in a way Dismiss never should.
               */
              <Button
                variant="go"
                disabled={microphone.asking}
                className={microphone.asking ? undefined : 'animate-attention'}
                onClick={() => void microphone.ask()}
              >
                {t('softphone.microphoneAccess.allow')}
              </Button>
            ) : (
              // Nothing to grant from here, so no green and no pulse: the fix is in the
              // browser's own settings or in a drawer, and the button only re-reads.
              <Button variant="outline" size="sm" onClick={() => void microphone.recheck()}>
                {t('softphone.microphoneAccess.recheck')}
              </Button>
            )
          }
        >
          {t(messageKey(microphone.readiness) ?? 'softphone.microphoneAccess.unknown')}
        </Banner>
      )}

      {telephone.takenOver !== null && (
        <Banner
          tone="warn"
          actions={
            <Button size="sm" variant="quiet" onClick={telephone.acknowledgeTakeover}>
              {t('softphone.dismiss')}
            </Button>
          }
        >
          {t('softphone.deskTakenOver', {
            name: telephone.takenOver.byName ?? '',
            desk: telephone.takenOver.deskName,
          })}
        </Banner>
      )}

      {/* The handset has gone quiet on the PBX: unplugged, or its cable pulled. Said here,
          because otherwise the first sign is a caller nobody answers. */}
      {telephone.onDesk && mine?.desk != null && !mine.deskReachable && (
        <Banner tone={mine.ringOtherWhenUnavailable ? 'warn' : 'danger'}>
          {mine.ringOtherWhenUnavailable
            ? t('softphone.deskUnreachableFallback', { desk: mine.desk.name })
            : t('softphone.deskUnreachable', { desk: mine.desk.name })}
        </Banner>
      )}

      {answerFailed && (
        <Banner
          tone="danger"
          actions={
            <Button size="sm" variant="quiet" onClick={acknowledgeAnswerFailure}>
              {t('softphone.dismiss')}
            </Button>
          }
        >
          {t('softphone.answerFailed')}
        </Banner>
      )}

      {lostOnReload && (
        <Banner
          tone="warn"
          actions={
            <Button size="sm" variant="quiet" onClick={acknowledgeLoss}>
              {t('softphone.dismiss')}
            </Button>
          }
        >
          {t('softphone.lostOnReload')}
        </Banner>
      )}

      {holdFailed !== null && (
        <Banner
          tone="danger"
          actions={
            <Button size="sm" variant="quiet" onClick={() => setHoldFailed(null)}>
              {t('softphone.dismiss')}
            </Button>
          }
        >
          {holdFailed}
        </Banner>
      )}

      {settings && (
        <>
          <TelephoneChoice />
          {(!telephone.onDesk || mine?.ringOtherWhenUnavailable === true) && (
            <DevicePicker devices={devices} onChange={chooseDevices} />
          )}
        </>
      )}

      <div className="px-3.5 py-2.5 flex flex-col gap-2">
        {deskShape && mine?.desk != null && (
          <>
            {deskCall === null ? (
              <p className="text-ink-3 text-secondary m-0">
                {t('softphone.ringsOnDesk', {
                  desk: mine.desk.name,
                  extension: mine.desk.extensions.join(', '),
                })}
              </p>
            ) : (
              <>
                <div className="flex items-baseline justify-between gap-2">
                  <strong className="font-semibold truncate">
                    {deskCall.from ?? t('calls.withheld')}
                  </strong>
                  {(deskCall.name ?? caller) != null && (deskCall.name ?? caller) !== '' && (
                    <span className="text-secondary text-ink-3 truncate">
                      {deskCall.name ?? caller}
                    </span>
                  )}
                </div>

                {/* Answering, hanging up and the keypad are on the handset. What the handset
                    cannot do is move the caller, and that is what these are. */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={holding}
                    onClick={() => void holdOnPbx(deskCall.callId)}
                  >
                    {t('softphone.hold')}
                  </Button>

                  {callFlow === 'HoldAndAnnounce' && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={orderOnCall == null || parking}
                      onClick={() => void park()}
                    >
                      {t('softphone.waitingForCar')}
                    </Button>
                  )}
                </div>

                {parkedNote !== null && (
                  <p className="text-secondary text-ink-3 m-0" role="status">
                    {parkedNote}
                  </p>
                )}

                {callFlow === 'RingBackOnly' && orderOnCall != null && (
                  <p className="text-secondary text-ink-3 m-0" role="status">
                    {t('softphone.willRingBack')}
                  </p>
                )}
              </>
            )}
          </>
        )}

        {/* The sentence the header's pill is too narrow for. Nobody can be on the line of a
            telephone that is not connected, so it takes the idle line's place. */}
        {!deskShape && call.state === 'idle' && (
          failure !== null ? (
            <p className="text-danger text-secondary m-0" role="status">{failure}</p>
          ) : (
            <p className="text-ink-3 text-secondary m-0">{t('softphone.idle')}</p>
          )
        )}

        {call.state === 'ringing' && (
          <>
            <div className="flex items-baseline justify-between gap-2" role="alert">
              <strong className="font-semibold truncate">{call.from}</strong>
              <span className="text-secondary text-ink-3">{t('softphone.ringing')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              {/* Answer is offered only for a call nobody has popped.
                *
                * On the ordinary path the caller is already on screen with their name and
                * their saved addresses, and taking them into the order form answers the
                * call — one gesture, because there is one intention. Two primary buttons
                * for one call read as a choice between talking and taking the order, which
                * is the misreading this removes.
                *
                * It cannot go altogether. A call can ring with no pop at all: an internal
                * extension, a driver ringing the desk, a caller whose lookup failed. Those
                * would be unanswerable. */}
              {!popped.some((number) => isSameCaller(number, call.from)) && (
                <Button size="sm" variant="primary" onClick={answer}>
                  {t('softphone.answer')}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={hangUp}>
                {t('softphone.reject')}
              </Button>
            </div>
          </>
        )}

        {call.state === 'calling' && (
          <div className="flex items-baseline justify-between gap-2">
            <span className="flex items-baseline gap-2 min-w-0">
              <strong className="font-semibold truncate">{call.to}</strong>
              <span className="text-secondary text-ink-3">{t('softphone.callingOut')}</span>
            </span>
            <Button size="sm" variant="quiet" onClick={hangUp}>
              {t('softphone.cancel')}
            </Button>
          </div>
        )}

        {call.state === 'up' && (
          <>
            <div className="flex items-baseline justify-between gap-2">
              <strong className="font-semibold truncate">{call.peer}</strong>
              {caller != null && caller !== '' && (
                <span className="text-secondary text-ink-3 truncate">{caller}</span>
              )}
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              <Button
                size="sm"
                variant={call.muted ? 'primary' : 'outline'}
                aria-pressed={call.muted}
                onClick={() => mute(!call.muted)}
              >
                {t('softphone.mute')}
              </Button>

              {/* On the PBX when the server knows this call as ours, which every call that
                  rang in does; held here only for a call placed from this browser. */}
              <Button
                size="sm"
                variant={call.held ? 'primary' : 'outline'}
                aria-pressed={call.held}
                disabled={holding}
                onClick={() =>
                  browserCall !== null ? void holdOnPbx(browserCall.callId) : hold(!call.held)
                }
              >
                {t('softphone.hold')}
              </Button>

              <Button
                size="sm"
                variant="outline"
                aria-expanded={keypad}
                onClick={() => setKeypad((open) => !open)}
              >
                {t('softphone.keypad')}
              </Button>

              {/* Only where the firm parks callers, and only once the order exists — there
                  is nothing for somebody to wait for otherwise, and the API says so. */}
              {callFlow === 'HoldAndAnnounce' && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={orderOnCall == null || parking}
                  onClick={() => void park()}
                >
                  {t('softphone.waitingForCar')}
                </Button>
              )}

              <Button size="sm" variant="danger" onClick={hangUp}>
                {t('softphone.hangUp')}
              </Button>
            </div>

            {/* The answer to "may I park this caller", in words the dispatcher acts on while
                the customer is still on the line. */}
            {parkedNote !== null && (
              <p className="text-secondary text-ink-3 m-0" role="status">
                {parkedNote}
              </p>
            )}

            {/* On the ring-back-only flow nobody is parked at all, so there is no button —
                just a reminder of what to say before hanging up. */}
            {callFlow === 'RingBackOnly' && orderOnCall != null && (
              <p className="text-secondary text-ink-3 m-0" role="status">
                {t('softphone.willRingBack')}
              </p>
            )}

            {keypad && <Keypad onPress={dtmf} />}

            <div className="flex items-center gap-1.5">
              <Input
                value={transferTo}
                onChange={(e) => setTransferTo(e.target.value)}
                placeholder={t('softphone.transferTo')}
                aria-label={t('softphone.transferTo')}
                inputMode="tel"
              />
              <Button
                size="sm"
                variant="outline"
                disabled={transferTo.trim() === ''}
                onClick={() => {
                  transfer(transferTo);
                  setTransferTo('');
                }}
              >
                {t('softphone.transfer')}
              </Button>
            </div>
          </>
        )}

        {/* A second caller while the first is up. Answering puts the first on hold on the
            PBX, where anybody can take them back — so a handset that manages two calls is
            enough, and nobody is left waiting in this dispatcher's ear. */}
        {waiting !== null && (
          <div className="flex flex-col gap-1.5 border-t border-rule pt-2" role="alert">
            <div className="flex items-baseline justify-between gap-2">
              <strong className="font-semibold truncate">{waiting.from}</strong>
              <span className="text-secondary text-ink-3">{t('softphone.secondCall')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="primary" onClick={takeSecond}>
                {t('softphone.answerSecond')}
              </Button>
              <Button size="sm" variant="outline" onClick={rejectWaiting}>
                {t('softphone.reject')}
              </Button>
            </div>
          </div>
        )}

        {heldHere !== null && (
          <div className="flex items-center justify-between gap-2 border-t border-rule pt-2">
            <span className="text-secondary text-ink-2 truncate">
              {t('softphone.heldHere', { peer: heldHere.peer })}
            </span>
            <Button size="sm" variant="outline" disabled={call.state !== 'idle'} onClick={resumeHeld}>
              {t('softphone.resume')}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Which telephone rings, and which desk — the dispatcher's own half of the settings.
 *
 * What rings on which line, and what a second caller does, are the office's and live in the
 * staff settings; these two are the dispatcher's, because they are about where they are
 * sitting and what is on their head.
 */
function TelephoneChoice() {
  const { t } = useTranslation();
  const telephone = useTelephoneContext();
  const { mine } = telephone;

  const [busy, setBusy] = useState(false);

  if (mine === null) return null;

  async function save(action: () => Promise<void>) {
    setBusy(true);

    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  const hasDesk = mine.desk !== null || mine.desks.length > 0;

  return (
    <div className="px-3.5 py-2.5 border-b border-rule flex flex-col gap-2">
      {hasDesk && (
        <label className="flex flex-col">
          <span className="text-secondary font-medium text-ink-2 mb-1">
            {t('softphone.answerOn')}
          </span>
          <Segmented<AnswerOn>
            className="self-start"
            label={t('softphone.answerOn')}
            value={mine.answerOn}
            options={[
              { value: 'Browser', label: t('softphone.answerOnBrowser') },
              { value: 'DeskPhone', label: t('softphone.answerOnDesk') },
            ]}
            onChange={(next) =>
              void save(() => telephone.chooseAnswerOn(next, mine.ringOtherWhenUnavailable))
            }
          />
        </label>
      )}

      {hasDesk && (
        <div className="flex items-center justify-between gap-3">
          <span className="text-secondary text-ink-2">
            {mine.answerOn === 'DeskPhone'
              ? t('softphone.ringBrowserWhenDeskUnreachable')
              : t('softphone.ringDeskWhenBrowserUnreachable')}
          </span>
          <Switch
            checked={mine.ringOtherWhenUnavailable}
            label={t('softphone.ringOther')}
            disabled={busy}
            onChange={(next) => void save(() => telephone.chooseAnswerOn(mine.answerOn, next))}
          />
        </div>
      )}

      {/* Where desks are chosen: which one this PC is at. Where desk phones belong to
          dispatchers, the administrator gave this person theirs and there is nothing to
          choose. */}
      {mine.deskPhonesBelongTo === 'Desk' && mine.desks.length > 0 && (
        <label className="flex flex-col">
          <span className="text-secondary font-medium text-ink-2 mb-1">{t('desks.desk')}</span>
          <Select
            value={mine.desk?.id ?? ''}
            disabled={busy}
            onChange={(e) =>
              void save(() => telephone.chooseDesk(e.target.value === '' ? null : e.target.value))
            }
          >
            <option value="">{t('desks.none')}</option>
            {mine.desks.map((desk) => (
              <option key={desk.id} value={desk.id}>
                {desk.takenBy === null
                  ? `${desk.name} · ${desk.extensions.join(', ')}`
                  : `${desk.name} · ${desk.extensions.join(', ')} — ${t('desks.takenBy', { name: desk.takenBy })}`}
              </option>
            ))}
          </Select>
        </label>
      )}

      {mine.deskPhonesBelongTo === 'Dispatcher' && mine.desk !== null && (
        <p className="m-0 text-secondary text-ink-3">
          {t('desks.yours', { desk: mine.desk.name, extension: mine.desk.extensions.join(', ') })}
        </p>
      )}
    </div>
  );
}

/**
 * What the telephone is doing, as one pill.
 *
 * A live call wins over the registration: "Ready · 201" is what the top bar is for, and the
 * only thing worth the card's header while somebody is talking is how long they have been.
 */
function State({ registration, call }: { registration: Phone['registration']; call: CallState }) {
  const { t } = useTranslation();
  const failure = useFailure(registration);

  if (call.state === 'up') {
    return (
      <Pill tone="ok" dot>
        {t('softphone.onACall')} · <Elapsed since={call.since} />
      </Pill>
    );
  }

  if (call.state === 'ringing') {
    return (
      <Pill tone="warn" dot>
        {t('softphone.ringing')}
      </Pill>
    );
  }

  const tone: Tone =
    registration.state === 'registered'
      ? 'ok'
      : registration.state === 'failed'
        ? 'danger'
        : registration.state === 'off'
          ? 'neutral'
          : 'warn';

  // A failure is a word here and a sentence in the card's body. The sentence is too wide for
  // a header in the half-width column, and a pill that will not shrink is drawn over the
  // title beside it.
  const words =
    registration.state === 'registered'
      ? t('softphone.registered', { extension: registration.extension })
      : registration.state === 'reconnecting'
        ? t('softphone.reconnecting')
        : registration.state === 'connecting'
          ? t('softphone.connecting')
          : registration.state === 'off'
            ? t('softphone.off')
            : t('softphone.offline');

  return (
    <Pill tone={tone} dot title={failure ?? undefined}>
      {words}
    </Pill>
  );
}

/**
 * Why the telephone could not connect, or null while it has not failed: the code,
 * translated. A dispatcher reading "the telephone system refused these credentials" can ring
 * the person who configured it; "something went wrong" gets reported to us instead.
 */
function useFailure(registration: Phone['registration']): string | null {
  const { t } = useTranslation();

  return registration.state === 'failed'
    ? t(`errors.${registration.code}`, { defaultValue: t('softphone.failed') })
    : null;
}

/** How long this call has been up. */
function Elapsed({ since }: { since: number }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);

    return () => clearInterval(timer);
  }, []);

  const seconds = Math.max(0, Math.floor((now - since) / 1000));
  const minutes = Math.floor(seconds / 60);

  return (
    <span className="tabular-nums">
      {minutes}:{String(seconds % 60).padStart(2, '0')}
    </span>
  );
}

const tones = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

function Keypad({ onPress }: { onPress: (tone: string) => void }) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-3 gap-1" role="group" aria-label={t('softphone.keypad')}>
      {tones.map((tone) => (
        <Button key={tone} size="sm" variant="outline" onClick={() => onPress(tone)}>
          {tone}
        </Button>
      ))}
    </div>
  );
}

/**
 * Which headset.
 *
 * Enumerated on demand rather than on mount, because a browser that has not been granted
 * microphone access reports devices with empty labels — a list of four things called
 * nothing. Opening this after the first call, by which time permission has been given, is
 * when the labels are worth reading.
 */
function DevicePicker({
  devices,
  onChange,
}: {
  devices: Devices;
  onChange: (devices: Devices) => void;
}) {
  const { t } = useTranslation();

  const [available, setAvailable] = useState<MediaDeviceInfo[]>([]);

  useEffect(() => {
    void navigator.mediaDevices
      ?.enumerateDevices()
      .then(setAvailable)
      .catch(() => setAvailable([]));
  }, []);

  const microphones = available.filter((device) => device.kind === 'audioinput');
  const speakers = available.filter((device) => device.kind === 'audiooutput');

  return (
    <div className="px-3.5 py-2.5 border-b border-rule flex flex-col gap-2">
      <label className="flex flex-col">
        <span className="text-secondary font-medium text-ink-2 mb-1">
          {t('softphone.microphone')}
        </span>
        <Select
          value={devices.microphoneId ?? ''}
          onChange={(e) =>
            onChange({ ...devices, microphoneId: e.target.value === '' ? null : e.target.value })
          }
        >
          <option value="">{t('softphone.systemDefault')}</option>
          {microphones.map((device) => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.label === '' ? device.deviceId : device.label}
            </option>
          ))}
        </Select>
      </label>

      {/* Only Chromium lets a page choose an output. Elsewhere the list is empty and the
          control is hidden rather than shown doing nothing. */}
      {speakers.length > 0 && (
        <label className="flex flex-col">
          <span className="text-secondary font-medium text-ink-2 mb-1">
            {t('softphone.speaker')}
          </span>
          <Select
            value={devices.speakerId ?? ''}
            onChange={(e) =>
              onChange({ ...devices, speakerId: e.target.value === '' ? null : e.target.value })
            }
          >
            <option value="">{t('softphone.systemDefault')}</option>
            {speakers.map((device) => (
              <option key={device.deviceId} value={device.deviceId}>
                {device.label === '' ? device.deviceId : device.label}
              </option>
            ))}
          </Select>
        </label>
      )}
    </div>
  );
}
